// ============================================================
// MUSIC THEORY ENGINE
// ============================================================

// Chromatic scale (sharps by default)
export const CHROMATIC       = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
export const CHROMATIC_SHARP = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
export const CHROMATIC_FLAT  = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];

// Keys displayed in cycle-of-fifths order
export const ALL_KEYS = ['C', 'G', 'D', 'A', 'E', 'B', 'F#', 'Db', 'Ab', 'Eb', 'Bb', 'F'];

// Standard guitar tuning (low → high). Swap this array for alternate tunings or instruments.
export const STANDARD_TUNING = ['E', 'A', 'D', 'G', 'B', 'E'];

export const TOTAL_FRETS = 24;

// Fret inlay positions (single dots + double at 12 & 24)
export const INLAY_FRETS = [3, 5, 7, 9, 12, 15, 17, 19, 21, 24];
export const DOUBLE_INLAY_FRETS = [12, 24];

// ---- Scale / chord formulas (semitone intervals from root) ----
export const SCALE_FORMULAS = {
  'CAGED SHAPES':      [0, 4, 7],                 // Major triad (root, major 3rd, perfect 5th)
  'MAJOR SCALE':       [0, 2, 4, 5, 7, 9, 11],
  'NATURAL MINOR':     [0, 2, 3, 5, 7, 8, 10],   // Natural Minor / Aeolian
  'MAJOR PENTATONIC':  [0, 2, 4, 7, 9],
  'MAJOR TRIAD':       [0, 4, 7],
  'TRIADS':            [0, 4, 7],
  '3 NOTES PER STRING':[0, 2, 4, 5, 7, 9, 11],   // Major scale with 3NPS positional logic
};

// ---- Diatonic modes (semitone intervals from root) ----
export const MODES = {
  'Ionian':     [0, 2, 4, 5, 7, 9, 11],  // Major
  'Dorian':     [0, 2, 3, 5, 7, 9, 10],
  'Phrygian':   [0, 1, 3, 5, 7, 8, 10],
  'Lydian':     [0, 2, 4, 6, 7, 9, 11],
  'Mixolydian': [0, 2, 4, 5, 7, 9, 10],
  'Aeolian':    [0, 2, 3, 5, 7, 8, 10],  // Natural Minor
  'Locrian':    [0, 1, 3, 5, 6, 8, 10],
};

export const MODE_NAMES = ['Ionian', 'Dorian', 'Phrygian', 'Lydian', 'Mixolydian', 'Aeolian', 'Locrian'];

// Scale degree labels — full chromatic map (all 12 pitch classes)
export const DEGREE_LABELS = {
  0: '1', 1: 'b2', 2: '2', 3: 'b3', 4: '3', 5: '4',
  6: 'b5', 7: '5', 8: 'b6', 9: '6', 10: 'b7', 11: '7',
};

// ---- Circle of Fifths Accidental Preference Engine ----

// Circle of fifths position for each pitch class (index 0-11) treated as a major key root.
// Positive → sharps, Negative → flats, 0 → no accidentals (C major).
// Index:  C   C#/Db  D  D#/Eb  E    F  F#/Gb  G  G#/Ab  A  A#/Bb  B
const COF_POSITION = [0, -5, 2, -3, 4, -1, 6, 1, -4, 3, -2, 5];

// Parent major key offset for heptatonic modes of the major scale.
// parentMajorRootIdx = (rootIdx + offset) % 12
const MODE_PARENT_OFFSETS = {
  '0,2,4,5,7,9,11': 0,   // Ionian (Major)
  '0,2,3,5,7,9,10': 10,  // Dorian
  '0,1,3,5,7,8,10': 8,   // Phrygian
  '0,2,4,6,7,9,11': 7,   // Lydian
  '0,2,4,5,7,9,10': 5,   // Mixolydian
  '0,2,3,5,7,8,10': 3,   // Aeolian (Natural Minor)
  '0,1,3,5,6,8,10': 1,   // Locrian
};

/**
 * Determine whether a key/formula/tonality combination should display flats.
 * Uses Circle of Fifths parent-key derivation — works for all 12 keys and
 * all standard modes without hardcoded key lists.
 *
 * For heptatonic formulas that match a known mode, the parent major key is
 * computed directly.  For pentatonics, triads, or unknown formulas, the
 * tonality parameter drives the parent key (minor → relative major = root+3).
 *
 * @param {string}   rootName  - root note name (e.g. 'G', 'Bb', 'F#')
 * @param {number[]|null} formula - scale/arpeggio interval formula, or null
 * @param {string}   tonality  - 'major' | 'minor'
 * @returns {boolean} true if flats should be used
 */
export function usesFlats(rootName, formula, tonality) {
  const rootIdx = noteIndex(rootName);
  if (rootIdx === -1) return false;

  let parentIdx;
  // For heptatonic formulas, try to match a known mode
  if (formula && formula.length === 7) {
    const offset = MODE_PARENT_OFFSETS[formula.join(',')];
    if (offset !== undefined) {
      parentIdx = (rootIdx + offset) % 12;
    }
  }
  // Fallback: derive parent major key from tonality
  if (parentIdx === undefined) {
    parentIdx = tonality === 'minor' ? (rootIdx + 3) % 12 : rootIdx;
  }

  const cofPos = COF_POSITION[parentIdx];

  // F#/Gb ambiguity (cofPos === 6): check if root name suggests a flat context
  if (cofPos === 6) {
    return rootName.includes('b');
  }

  return cofPos < 0;
}

/**
 * Get the display name for a pitch class (0-11).
 * @param {number}  semitoneIdx - pitch class 0-11
 * @param {boolean} flats       - true → prefer flat names, false → sharp names
 * @returns {string}
 */
export function getNoteName(semitoneIdx, flats) {
  const idx = ((semitoneIdx % 12) + 12) % 12;
  return flats ? CHROMATIC_FLAT[idx] : CHROMATIC_SHARP[idx];
}

// ---- Helpers ----

/** Get the chromatic index (0-11) for a note name, supporting both sharps and flats */
export function noteIndex(name) {
  let idx = CHROMATIC_SHARP.indexOf(name);
  if (idx !== -1) return idx;
  idx = CHROMATIC_FLAT.indexOf(name);
  if (idx !== -1) return idx;
  return -1;
}

/** Build the set of note names in a scale for a given key + formula */
export function scaleNotes(key, formula, tonality = 'major') {
  const root = noteIndex(key);
  const flats = usesFlats(key, formula, tonality);
  return formula.map(interval => getNoteName((root + interval) % 12, flats));
}

/** Return the interval (semitones from root) for a note in a given key, or -1 */
export function intervalFromRoot(key, noteName) {
  const root = noteIndex(key);
  const n = noteIndex(noteName);
  if (root === -1 || n === -1) return -1;
  return (n - root + 12) % 12;
}

// ---- Fretboard matrix builder ----

/**
 * Generate a 2D array [string][fret] where each cell = { noteName, interval, stringIndex, fretIndex, isInScale }
 * All interval math uses modulo-12 integers; note names are assigned at the end via getNoteName().
 * @param {string} key - selected key (e.g. 'G')
 * @param {number[]} formula - interval formula array
 * @param {string[]} tuning - array of open-string note names
 * @param {string} tonality - 'major' | 'minor' (used to derive flat vs. sharp spelling)
 */
export function buildFretboardMatrix(key, formula, tuning = STANDARD_TUNING, tonality = 'major') {
  const formulaSet = new Set(formula);
  const rootIdx    = noteIndex(key);
  const flats      = usesFlats(key, formula, tonality);
  const matrix     = [];

  for (let s = 0; s < tuning.length; s++) {
    const openIdx = noteIndex(tuning[s]);
    const row = [];
    for (let f = 0; f <= TOTAL_FRETS; f++) {
      const pitch    = (openIdx + f) % 12;
      const interval = (pitch - rootIdx + 12) % 12;
      row.push({
        noteName:    getNoteName(pitch, flats),
        interval,
        scaleDegree: DEGREE_LABELS[interval] || null,
        isRoot:      interval === 0,
        isInScale:   formulaSet.has(interval),
        stringIndex: s,
        fretIndex:   f,
      });
    }
    matrix.push(row);
  }
  return matrix;
}

// ============================================================
// CAGED SHAPE BOUNDARIES
// ============================================================

/**
 * CAGED shapes are defined by their relationship to each other.
 * The shapes cycle in order: C - A - G - E - D  (repeating).
 * For a given key, the "base fret" of each shape shifts.
 *
 * Each shape's window is stored relative to the root on the low-E string (below).
 */

// Each shape is anchored on the fret of the root on the low-E string ("rootE"). `offset` moves
// to the shape's barre fret b (where the open chord's nut would sit), and `from`/`to` give the
// frets, relative to b, that its scale position covers. Worked out in G (rootE = 3):
//   E shape: b = 3,  frets 2-5     D shape: b = 5,  frets 4-8     C shape: b = 7, frets 7-10
//   A shape: b = 10, frets 9-13    G shape: b = 12, frets 11-15   (then E again at 14-17)
// Neighbouring shapes share a fret at their seams, as they do on the neck. The same windows
// are used for minor keys (Em, Dm, Cm, Am, Gm forms sit in the same places relative to the root).
const CAGED_SHAPES = {
  'E SHAPE': { offset: 0, from: -1, to: 2 },
  'D SHAPE': { offset: 2, from: -1, to: 3 },
  'C SHAPE': { offset: 4, from: 0,  to: 3 },
  'A SHAPE': { offset: 7, from: -1, to: 3 },
  'G SHAPE': { offset: 9, from: -1, to: 3 },
};

/**
 * Fret ranges for a CAGED shape in a given key, one per octave up the neck.
 * Returns an array of { start, end } (inclusive) clipped to the neck, or null for an unknown shape.
 */
export function getShapeFretRange(key, shapeName) {
  const shape = CAGED_SHAPES[shapeName];
  if (!shape) return null;
  const rootOnLowE = (noteIndex(key) - noteIndex('E') + 12) % 12;
  const barre = (rootOnLowE + shape.offset) % 12;

  // Start an octave down so a shape whose window wraps below the nut (e.g. G shape in F,
  // frets 0-1) is still caught.
  const ranges = [];
  for (let b = barre - 12; b + shape.from <= TOTAL_FRETS; b += 12) {
    const start = Math.max(0, b + shape.from);
    const end = Math.min(TOTAL_FRETS, b + shape.to);
    if (end >= 0) ranges.push({ start, end });
  }
  return ranges;
}

/**
 * Check if a fret falls within any active CAGED shape for the given key.
 * @param {number} fret
 * @param {string} key
 * @param {string[]} activeShapes - array of active shape names
 * @returns {boolean}
 */
export function isInActiveShape(fret, key, activeShapes) {
  if (!activeShapes || activeShapes.length === 0) return true; // No filter = show all zones

  for (const shape of activeShapes) {
    if (shape === 'CLOSED') continue; // 'CLOSED' = all frets (no open strings)
    const ranges = getShapeFretRange(key, shape);
    if (!ranges) continue;
    for (const r of ranges) {
      if (fret >= r.start && fret <= r.end) return true;
    }
  }
  return false;
}

// ---- 3 Notes Per String positions ----

/**
 * Build a Set of "stringIndex-fretIndex" positions for a specific 3NPS pattern.
 *
 * The 7 standard 3NPS patterns each start on a different scale degree of the
 * heptatonic scale.  For each string, exactly 3 consecutive scale degrees are
 * assigned in ascending fret order.  The degree cursor advances by 3 per string.
 *
 * @param {string}   key           - root key (e.g. 'G')
 * @param {number[]} formula       - heptatonic scale formula (7 intervals)
 * @param {number}   patternNumber - 1-7 for a specific pattern; 0 = all positions
 * @param {string[]} tuning        - open-string note names (low → high)
 * @returns {Set<string>}
 */
export function build3NPSPositions(key, formula, patternNumber = 0, tuning = STANDARD_TUNING) {
  const positions = new Set();
  const rootIdx = noteIndex(key);

  // Non-heptatonic scale or "All Patterns" → return every scale note on the neck
  if (patternNumber === 0 || formula.length !== 7) {
    const matrix = buildFretboardMatrix(key, formula, tuning);
    for (let s = 0; s < tuning.length; s++) {
      for (let f = 0; f <= TOTAL_FRETS; f++) {
        if (matrix[s][f].isInScale) positions.add(`${s}-${f}`);
      }
    }
    return positions;
  }

  // Walk through each string, picking 3 consecutive scale degrees per string
  let degreeIdx = patternNumber - 1; // patterns are 1-indexed; degrees are 0-indexed

  for (let s = 0; s < tuning.length; s++) {
    const openIdx = noteIndex(tuning[s]);
    let minFret = 0; // enforce ascending fret order within a string

    for (let i = 0; i < 3; i++) {
      const interval = formula[degreeIdx % 7];
      const targetNoteIdx = (rootIdx + interval) % 12;

      // Lowest fret on this string where the target note appears
      let fret = ((targetNoteIdx - openIdx + 12) % 12);
      if (fret < minFret) fret += 12; // wrap to next octave if needed

      if (fret <= TOTAL_FRETS) {
        positions.add(`${s}-${fret}`);
        minFret = fret + 1;
      }

      degreeIdx++;
    }
    // degreeIdx has advanced by 3 — correct starting degree for the next string
  }

  return positions;
}

// ============================================================
// ARPEGGIOS
// ============================================================

/**
 * Arpeggio formula dictionary — keyed by 'Type:Tonality'.
 * `intervals` = semitone offsets from the ACTUAL chord root (no substitute-
 * triad trickery). `labels` maps each offset → degree label.
 *
 * Tonality-agnostic types (Sus2, Sus4, Dim, Dim7, Aug, Dom7, Dom9, m7b5)
 * appear under both :Major and :Minor so `Type:${tonality}` always resolves.
 * Tonality-variant types come in name pairs (Maj6/Min6, Add9/mAdd9, 6/9/m6/9…).
 */
export const ARPEGGIO_DICT = {
  'Major:Major': { intervals: [0, 4, 7],      labels: { 0: '1', 4: '3', 7: '5' } },
  'Minor:Minor': { intervals: [0, 3, 7],      labels: { 0: '1', 3: 'b3', 7: '5' } },
  'Sus2:Major':  { intervals: [0, 2, 7],      labels: { 0: '1', 2: '2', 7: '5' } },
  'Sus2:Minor':  { intervals: [0, 2, 7],      labels: { 0: '1', 2: '2', 7: '5' } },
  'Sus4:Major':  { intervals: [0, 5, 7],      labels: { 0: '1', 5: '4', 7: '5' } },
  'Sus4:Minor':  { intervals: [0, 5, 7],      labels: { 0: '1', 5: '4', 7: '5' } },
  'Dim:Major':   { intervals: [0, 3, 6],      labels: { 0: '1', 3: 'b3', 6: 'b5' } },
  'Dim:Minor':   { intervals: [0, 3, 6],      labels: { 0: '1', 3: 'b3', 6: 'b5' } },
  'Dim7:Major':  { intervals: [0, 3, 6, 9],   labels: { 0: '1', 3: 'b3', 6: 'b5', 9: 'bb7' } },
  'Dim7:Minor':  { intervals: [0, 3, 6, 9],   labels: { 0: '1', 3: 'b3', 6: 'b5', 9: 'bb7' } },
  'Aug:Major':   { intervals: [0, 4, 8],      labels: { 0: '1', 4: '3', 8: '#5' } },
  'Aug:Minor':   { intervals: [0, 4, 8],      labels: { 0: '1', 4: '3', 8: '#5' } },
  'Maj6:Major':  { intervals: [0, 4, 7, 9],   labels: { 0: '1', 4: '3', 7: '5', 9: '6' } },
  'Min6:Minor':  { intervals: [0, 3, 7, 9],   labels: { 0: '1', 3: 'b3', 7: '5', 9: '6' } },
  'Maj7:Major':  { intervals: [0, 4, 7, 11],  labels: { 0: '1', 4: '3', 7: '5', 11: '7' } },
  'Min7:Minor':  { intervals: [0, 3, 7, 10],  labels: { 0: '1', 3: 'b3', 7: '5', 10: 'b7' } },
  'Dom7:Major':  { intervals: [0, 4, 7, 10],  labels: { 0: '1', 4: '3', 7: '5', 10: 'b7' } },
  'Dom7:Minor':  { intervals: [0, 4, 7, 10],  labels: { 0: '1', 4: '3', 7: '5', 10: 'b7' } },
  'Maj9:Major':  { intervals: [0, 4, 7, 11, 2], labels: { 0: '1', 4: '3', 7: '5', 11: '7', 2: '9' } },
  'Min9:Minor':  { intervals: [0, 3, 7, 10, 2], labels: { 0: '1', 3: 'b3', 7: '5', 10: 'b7', 2: '9' } },
  'Dom9:Major':  { intervals: [0, 4, 7, 10, 2], labels: { 0: '1', 4: '3', 7: '5', 10: 'b7', 2: '9' } },
  'Dom9:Minor':  { intervals: [0, 4, 7, 10, 2], labels: { 0: '1', 4: '3', 7: '5', 10: 'b7', 2: '9' } },
  'm7b5:Major':  { intervals: [0, 3, 6, 10],    labels: { 0: '1', 3: 'b3', 6: 'b5', 10: 'b7' } },
  'm7b5:Minor':  { intervals: [0, 3, 6, 10],    labels: { 0: '1', 3: 'b3', 6: 'b5', 10: 'b7' } },
  'Add9:Major':  { intervals: [0, 4, 7, 2],     labels: { 0: '1', 4: '3', 7: '5', 2: '9' } },
  'mAdd9:Minor': { intervals: [0, 3, 7, 2],     labels: { 0: '1', 3: 'b3', 7: '5', 2: '9' } },
  '6/9:Major':   { intervals: [0, 4, 7, 9, 2],  labels: { 0: '1', 4: '3', 7: '5', 9: '6', 2: '9' } },
  'm6/9:Minor':  { intervals: [0, 3, 7, 9, 2],  labels: { 0: '1', 3: 'b3', 7: '5', 9: '6', 2: '9' } },
};

/**
 * Arpeggio type names offered in the UI, split into the two rows the controls
 * render. Tonality-variant types swap name with the toggle (Maj7 ↔ Min7);
 * tonality-agnostic ones (Sus2, Dom7, Dim7…) are spelled the same either way.
 *
 * This is the single source of truth for the type lists — the guitar controls,
 * the fiddle controls and the App-level validation in handle*StructureChange
 * all read it, so adding a type here is enough to add it everywhere.
 *
 * @param {string} tonality - 'Major' | 'Minor'
 * @returns {{basic: string[], extensions: string[], all: string[]}}
 */
export function arpeggioTypesFor(tonality) {
  const isMajor = tonality !== 'Minor';
  const basic = [isMajor ? 'Major' : 'Minor', 'Sus2', 'Sus4', 'Dim', 'Aug'];
  const extensions = [
    isMajor ? 'Maj6' : 'Min6',
    isMajor ? 'Maj7' : 'Min7',
    isMajor ? 'Maj9' : 'Min9',
    isMajor ? 'Add9' : 'mAdd9',
    isMajor ? '6/9'  : 'm6/9',
    'Dom7',
    'Dom9',
    'Dim7',
    'm7b5',
  ];
  return { basic, extensions, all: [...basic, ...extensions] };
}

/**
 * Types whose NAME changes with the tonality toggle. Flipping Major↔Minor while
 * an arpeggio is selected maps the type through this so the choice survives the
 * flip (Maj7 → Min7) instead of snapping back to the plain triad. Types absent
 * from the map are tonality-agnostic and stay put.
 */
export const ARPEGGIO_TYPE_SWAP = {
  'Major': 'Minor', 'Minor': 'Major',
  'Maj6':  'Min6',  'Min6':  'Maj6',
  'Maj7':  'Min7',  'Min7':  'Maj7',
  'Maj9':  'Min9',  'Min9':  'Maj9',
  'Add9':  'mAdd9', 'mAdd9': 'Add9',
  '6/9':   'm6/9',  'm6/9':  '6/9',
};

// Diatonic-spelling tables for chord-tone note names.
const LETTER_SEQ  = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const LETTER_PC   = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
// Letters above the root implied by each degree label (3rd = 2 letters up, etc.)
const DEGREE_STEP = {
  '1': 0,
  '2': 1, '9': 1,
  'b3': 2, '3': 2,
  '4': 3,
  'b5': 4, '5': 4, '#5': 4,
  '6': 5,
  'b7': 6, '7': 6, 'bb7': 6,
};

/**
 * Spell a chord tone with the letter + accidental that matches its degree, so a
 * b3 always reads as a flatted 3rd letter (G → Bb, never A#) and a bb7 reads as
 * a double-flatted 7th (G → Fb). Diminished/augmented arpeggios in some keys
 * therefore produce double accidentals (e.g. Eb°7 → Dbb) — that is correct
 * notation, not a bug. Falls back to a plain flat spelling for unknown labels.
 *
 * @param {string} rootName    - chord root note name (e.g. 'G', 'Bb', 'F#')
 * @param {number} offset      - semitone offset of the tone from the root
 * @param {string} degreeLabel - degree label from ARPEGGIO_DICT (e.g. 'b3', 'bb7')
 * @returns {string} spelled note name (e.g. 'Bb', 'F#', 'Dbb')
 */
export function spellChordTone(rootName, offset, degreeLabel) {
  const rootIdx = noteIndex(rootName);
  if (rootIdx === -1) return '';
  const targetPc = (rootIdx + offset) % 12;
  const step = DEGREE_STEP[degreeLabel];
  if (step === undefined) return getNoteName(targetPc, true); // fallback: prefer flats
  const letter = LETTER_SEQ[(LETTER_SEQ.indexOf(rootName[0]) + step) % 7];
  let acc = ((targetPc - LETTER_PC[letter]) % 12 + 12) % 12;  // 0..11
  if (acc > 6) acc -= 12;                                     // nearest accidental
  const suffix = acc === 2 ? '##' : acc === 1 ? '#' : acc === -1 ? 'b' : acc === -2 ? 'bb' : '';
  return letter + suffix;
}

/**
 * Build every position of every chord tone (3, 4, or 5 notes) across the
 * full neck on active strings — a true full-neck arpeggio map (no windowed
 * voicing/inversion logic).
 *
 * @param {object}    params
 * @param {string}    params.key          - chord root note name (e.g. 'G')
 * @param {string}    params.type         - arpeggio type key (e.g. 'Major', 'Dom7')
 * @param {string}    params.tonality     - 'Major' | 'Minor'
 * @param {string[]}  params.tuning       - open-string note names low-to-high
 * @param {boolean[]} params.stringFocus  - one boolean per string; any combination allowed
 * @param {number}    params.totalFrets   - highest fret to search (default 24)
 * @returns {Array<{string, fret, pitchClass, interval, isRoot}>}
 */
export function buildArpeggioNotes({
  key, type, tonality,
  tuning = STANDARD_TUNING,
  stringFocus,
  totalFrets = TOTAL_FRETS,
}) {
  const entry = ARPEGGIO_DICT[`${type}:${tonality}`];
  if (!entry) return [];
  const rootIdx = noteIndex(key);
  if (rootIdx === -1) return [];
  const { intervals, labels } = entry;
  const pcToLabel = {};             // absolute pitch class → degree label
  const pcToName  = {};             // absolute pitch class → spelled note name
  intervals.forEach(off => {
    const pc = (off + rootIdx) % 12;
    pcToLabel[pc] = labels[off];
    pcToName[pc]  = spellChordTone(key, off, labels[off]);
  });

  const nodes = [];
  for (let s = 0; s < tuning.length; s++) {
    if (stringFocus && !stringFocus[s]) continue;
    const openPc = noteIndex(tuning[s]);
    for (let fret = 0; fret <= totalFrets; fret++) {
      const pc = (openPc + fret) % 12;
      if (pc in pcToLabel) {
        nodes.push({ string: s, fret, pitchClass: pc, interval: pcToLabel[pc], noteName: pcToName[pc], isRoot: pc === rootIdx });
      }
    }
  }
  return nodes;
}
